import { motion, useMotionValue, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { useLang } from "@/context/LangContext";

const THRESHOLD = 90;

export const DecisionCard = ({ card, onChoose, onDrag }) => {
  const { lang } = useLang();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-250, 250], [-14, 14]);
  const leftOp = useTransform(x, [-THRESHOLD, -20], [1, 0]);
  const rightOp = useTransform(x, [20, THRESHOLD], [0, 1]);

  return (
    <motion.div
      className="decision-card absolute inset-0 p-5 sm:p-6 flex flex-col select-none cursor-grab active:cursor-grabbing"
      style={{ x, rotate }}
      drag="x"
      dragElastic={0.9}
      dragConstraints={{ left: 0, right: 0 }}
      onDrag={(_, info) => onDrag(info.offset.x)}
      onDragEnd={(_, info) => {
        onDrag(0);
        if (info.offset.x < -THRESHOLD) onChoose("left");
        else if (info.offset.x > THRESHOLD) onChoose("right");
      }}
      initial={{ scale: 0.92, opacity: 0, y: 30 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ opacity: 0, transition: { duration: 0.25 } }}
      transition={{ type: "spring", stiffness: 260, damping: 24 }}
      data-testid="decision-card"
    >
      <motion.div style={{ opacity: leftOp }} className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-display font-extrabold uppercase tracking-wide text-white" data-testid="swipe-left-label">
        <span className="bg-[#E11C2A] px-3 py-1.5 rounded-full inline-flex items-center gap-1"><ChevronLeft size={14} />{card.left.text[lang]}</span>
      </motion.div>
      <motion.div style={{ opacity: rightOp }} className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-display font-extrabold uppercase tracking-wide text-white" data-testid="swipe-right-label">
        <span className="bg-[#10B981] px-3 py-1.5 rounded-full inline-flex items-center gap-1">{card.right.text[lang]}<ChevronRight size={14} /></span>
      </motion.div>

      <div className="flex flex-col items-center mt-6">
        <Avatar speaker={card.speaker} size={112} />
        <div className="mt-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[#7c2d12]" data-testid="card-speaker">{card.name[lang]}</div>
        {card.source === "ai" && <span className="mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[#F2A900]/20 text-[#7c2d12] font-bold" data-testid="card-ai-badge">AI</span>}
      </div>
      <p className="font-display font-bold text-lg sm:text-xl leading-snug text-center mt-4 flex-1 flex items-center justify-center px-1" data-testid="card-text">{card.text[lang]}</p>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <button onClick={() => onChoose("left")} onPointerEnter={() => onDrag(-THRESHOLD)} onPointerLeave={() => onDrag(0)} className="rounded-2xl bg-[#1A0507] text-[var(--text-light)] py-3 px-2 text-sm font-bold hover:bg-[#E11C2A] transition-colors duration-200 flex items-center justify-center gap-1" data-testid="card-swipe-left-btn">
          <ChevronLeft size={16} /> {card.left.text[lang]}
        </button>
        <button onClick={() => onChoose("right")} onPointerEnter={() => onDrag(THRESHOLD)} onPointerLeave={() => onDrag(0)} className="rounded-2xl bg-[#1A0507] text-[var(--text-light)] py-3 px-2 text-sm font-bold hover:bg-[#10B981] transition-colors duration-200 flex items-center justify-center gap-1" data-testid="card-swipe-right-btn">
          {card.right.text[lang]} <ChevronRight size={16} />
        </button>
      </div>
    </motion.div>
  );
};
